import type {
    IdibuPortal,
    IdibuOrgConfig,
    IdibuConfigResponse,
    PortalCategory,
    PortalCountry,
    PortalSubscribeInfoResponse,
    BoardPublishing,
    PublishResult,
    ProvisionData,
    ApplicantSyncResult,
    ReconcileResult,
    ProvisionedAccount,
} from '../services/idibu.api';

import {
    DEMO_PORTALS,
    DEMO_CATEGORIES,
    DEMO_COUNTRIES,
    DEMO_CONFIG,
    DEMO_SUBSCRIBED_IDS,
} from './demo-fixtures';

/**
 * In-memory implementation of the `idibuApi` surface used when
 * `IDIBU_DEMO_MODE` is true. Returns fixture data wrapped in the same
 * `{ data }` envelope axios produces, so consumers don't need to know
 * they're talking to a mock.
 *
 * Mutable state (subscribed IDs, config) lives at module scope so the
 * page reflects subscribe/unsubscribe actions within a single session.
 * It resets on full page reload — that's intentional, no persistence.
 */

const PORTALS = DEMO_PORTALS.map(({ popularRank: _omit, ...p }) => p);
const POPULAR_RANK = new Map<number, number>(
    DEMO_PORTALS.map((p) => [p.id, p.popularRank]),
);

let subscribedIds = new Set<number>(DEMO_SUBSCRIBED_IDS);
let config: IdibuOrgConfig = { ...DEMO_CONFIG };

/** Wrap a value in the axios `{ data }` envelope after a small delay. */
function envelope<T>(data: T, delayMs = 220): Promise<{ data: T }> {
    return new Promise((resolve) =>
        setTimeout(() => resolve({ data }), delayMs),
    );
}

function sortBySubscribedThenPopular(a: IdibuPortal, b: IdibuPortal): number {
    const aSub = subscribedIds.has(a.id) ? 0 : 1;
    const bSub = subscribedIds.has(b.id) ? 0 : 1;
    if (aSub !== bSub) return aSub - bSub;
    const aRank = POPULAR_RANK.get(a.id) ?? 999;
    const bRank = POPULAR_RANK.get(b.id) ?? 999;
    return aRank - bRank;
}

export const demoIdibuApi = {
    listSubscribedPortals: () => {
        const portals = PORTALS.filter((p) => subscribedIds.has(p.id)).sort(
            sortBySubscribedThenPopular,
        );
        return envelope({ portals });
    },

    listAllPortals: (params?: {
        count?: number;
        offset?: number;
        filter_name?: string;
        filter_country?: string;
        filter_tags?: string;
    }) => {
        const count = params?.count ?? 20;
        const offset = params?.offset ?? 0;
        const nameFilter = params?.filter_name?.toLowerCase().trim();
        const countryFilter = params?.filter_country;
        const tagFilter = params?.filter_tags;

        const filtered = PORTALS.filter((p) => {
            if (nameFilter && !p.name.toLowerCase().includes(nameFilter)) return false;
            if (countryFilter && p.country !== countryFilter) return false;
            if (tagFilter && !p.tags.split(';').includes(tagFilter)) return false;
            return true;
        }).sort(sortBySubscribedThenPopular);

        return envelope({ portals: filtered.slice(offset, offset + count) });
    },

    searchLocations: (_countryCode: string, _filterName: string, _count?: number) =>
        envelope({ locations: [] }),

    publishJob: (_data: unknown) =>
        envelope<PublishResult>({
            pcpUrl: null,
            jobId: 'demo-job',
            idibuResponse: { ok: true },
        }),

    updateJob: (_data: unknown) =>
        envelope<PublishResult>({
            pcpUrl: null,
            jobId: 'demo-job',
            idibuResponse: { ok: true },
        }),

    deleteJob: (_jobPostingId: string) => envelope({ deleted: true }),

    deletePost: (_jobPostingId: string, _portalId: number) =>
        envelope<{ boardPublishing: BoardPublishing }>({
            boardPublishing: {
                provider: 'idibu',
                providerJobId: null,
                providerPostingId: null,
                status: 'draft',
                pcpUrl: null,
                posts: [],
                lastSyncedAt: null,
            },
        }),

    getPostingStatus: (_jobPostingId: string) =>
        envelope<{ boardPublishing: BoardPublishing | null }>({
            boardPublishing: null,
        }),

    syncPostingStatus: (_jobPostingId: string) =>
        envelope<{ boardPublishing: BoardPublishing }>({
            boardPublishing: {
                provider: 'idibu',
                providerJobId: null,
                providerPostingId: null,
                status: 'draft',
                pcpUrl: null,
                posts: [],
                lastSyncedAt: new Date().toISOString(),
            },
        }),

    getConfig: () =>
        envelope<IdibuConfigResponse>({
            config,
            partnerAvailable: false,
        }),

    updateConfig: (data: { accountHash: string }) => {
        config = {
            ...config,
            accountHash: data.accountHash || config.accountHash,
            isActive: true,
        };
        return envelope({ config });
    },

    reconnect: () => {
        config = { ...config, isActive: true };
        return envelope({ config });
    },

    provisionAccount: (_data: ProvisionData) => {
        config = { ...config, isActive: true };
        return envelope({ config });
    },

    disconnectConfig: () => {
        config = { ...config, isActive: false };
        return envelope({ disconnected: true });
    },

    getPortalSubscribeInfo: (_portalId: number) =>
        envelope<PortalSubscribeInfoResponse>({ help: null, fields: [] }),

    subscribePortal: (portalId: number, _fields: { name: string; value: string }[]) => {
        subscribedIds = new Set(subscribedIds).add(portalId);
        return envelope({ subscribed: true });
    },

    unsubscribePortal: (portalId: number) => {
        const next = new Set(subscribedIds);
        next.delete(portalId);
        subscribedIds = next;
        return envelope({ unsubscribed: true });
    },

    getPortalCategories: () =>
        envelope<{ categories: PortalCategory[] }>({ categories: DEMO_CATEGORIES }),

    getPortalCountries: () =>
        envelope<{ countries: PortalCountry[] }>({ countries: DEMO_COUNTRIES }),

    listProvisionedAccounts: (_params?: { count?: number; offset?: number }) =>
        envelope<{ accounts: ProvisionedAccount[] }>({ accounts: [] }),

    syncApplicantStatus: (_applicationId: string) =>
        envelope<ApplicantSyncResult>({ skipped: true, reason: 'demo-mode' }),

    bulkSyncStatuses: (_jobPostingId: string) =>
        envelope({ total: 0, synced: 0, alreadySynced: 0, noAction: 0, failed: 0 }),

    reconcile: (_data?: { afterDate?: string; allTime?: boolean; dryRun?: boolean }) =>
        envelope<ReconcileResult>({
            total: 0,
            created: 0,
            existing: 0,
            skipped: 0,
            errors: 0,
        }),
};
